<template>
    <GeneralDashboardLayout>
        <template v-slot:header>
            <div class="header-template">
                <BackButton :to="{ name: 'event_dashboard.index' }" />
                <h1 class="title text-center">Virtual Tour</h1>
            </div>
        </template>
        
        <template v-slot:content>
            <div class="virtual-tour-management">
                <div class="container-fluid">
                    <!-- Header -->
                    <div class="row mb-4">
                        <div class="col-12">
                            <div class="card">
                                <div class="card-body">
                                    <h4 class="card-title">Virtual Tour Management</h4>
                                    <button 
                                        v-if="!virtualTour" 
                                        class="btn btn-primary" 
                                        @click="createNewTour"
                                        :disabled="loading"
                                    >
                                        <span v-if="loading">Creating...</span>
                                        <span v-else>Create Virtual Tour</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Only Schedules Tab now (Organizers removed) -->
                    <div class="row" v-if="virtualTour">
                        <div class="col-12">
                            <ul class="nav nav-tabs">
                                <li class="nav-item">
                                    <a class="nav-link" :class="{ active: activeTab === 'schedules' }" 
                                    @click="activeTab = 'schedules'" href="javascript:void(0)">
                                        <i class="fas fa-calendar"></i> Tour Schedules
                                    </a>
                                </li>
                                <li class="nav-item">
                                    <a class="nav-link" :class="{ active: activeTab === 'audience' }" 
                                    @click="activeTab = 'audience'" href="javascript:void(0)">
                                        <i class="fas fa-eye"></i> Audience
                                    </a>
                                </li>
                            </ul>
                        </div>
                    </div>

                    <!-- Schedules Tab (Updated with organizer column) -->
                    <div class="row mt-4" v-if="activeTab === 'schedules' && virtualTour">
                        <div class="col-12">
                            <div class="card">
                                <div class="card-header d-flex justify-content-between align-items-center">
                                    <h5 class="mb-0">Tour Schedules</h5>
                                    <button class="btn btn-sm btn-primary" @click="openScheduleModal()" :disabled="loading">
                                        <i class="fas fa-plus"></i> Add Schedule
                                    </button>
                                </div>
                                <div class="card-body">
                                    <div class="table-responsive">
                                        <table class="table table-bordered">
                                            <thead>
                                                <tr>
                                                    <th>Tour Name</th>
                                                    <th>Guide/Organizer</th>
                                                    <th>Start Date & Time</th>
                                                    <th>End Date & Time</th>
                                                    <th>Duration</th>
                                                    <th>Attendees</th>
                                                    <th>Status</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr v-for="schedule in schedules" :key="schedule._id">
                                                    <td>{{ schedule.tour_name }}</td>
                                                    <td>
                                                        <div class="d-flex align-items-center">
                                                            <img 
                                                                :src="getOrganizerImageUrl(schedule.organizer)" 
                                                                :alt="schedule.organizer?.name"
                                                                class="profile-img-sm rounded-circle me-2"
                                                                @error="handleImageError"
                                                            />
                                                            <div>
                                                                <strong>{{ schedule.organizer?.name || 'Not Assigned' }}</strong>
                                                                <br>
                                                                <small class="text-muted">{{ schedule.organizer?.email || '' }}</small>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>{{ formatDateTime(schedule.start_date, eventTimezone) }}</td>
                                                    <td>{{ formatDateTime(schedule.end_date, eventTimezone) }}</td>
                                                    <td>{{ schedule.duration }} mins</td>
                                                    <td>
                                                        {{ schedule.attendees?.length || 0 }} / {{ schedule.max_attendees || 'Unlimited' }}
                                                        <button class="btn btn-sm btn-success ms-2" @click="openAddAttendeeModal(schedule)" :disabled="loading">
                                                            <i class="fas fa-user-plus"></i>
                                                        </button>
                                                    </td>
                                                    <td>
                                                        <span :class="'badge bg-' + getStatusColor(schedule.status)">
                                                            {{ schedule.status }}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <button class="btn btn-sm btn-info me-1" @click="openScheduleModal(schedule)" :disabled="loading">
                                                            <i class="fas fa-edit"></i>
                                                        </button>
                                                        <button class="btn btn-sm btn-danger" @click="deleteSchedule(schedule._id)" :disabled="loading">
                                                            <i class="fas fa-trash"></i>
                                                        </button>
                                                    </td>
                                                </tr>
                                                <tr v-if="schedules.length === 0">
                                                    <td colspan="8" class="text-center">No schedules added</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Audience Tab (unchanged) -->
                    <div class="row mt-4" v-if="activeTab === 'audience' && virtualTour">
                        <div class="col-12">
                            <div class="card">
                                <div class="card-header d-flex justify-content-between align-items-center">
                                    <h5 class="mb-0">Tour Audience ({{ audience.length }} participants)</h5>
                                    <button class="btn btn-sm btn-primary" @click="openAddAudienceModal" :disabled="loading">
                                        <i class="fas fa-plus"></i> Add Audience Member
                                    </button>
                                </div>
                                <div class="card-body">
                                    <div class="table-responsive">
                                        <table class="table table-bordered">
                                            <thead>
                                                <tr>
                                                    <th>Profile</th>
                                                    <th>Name</th>
                                                    <th>Email</th>
                                                    <th>Schedules Attended</th>
                                                    <th>Registered Date</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr v-for="attendee in audience" :key="attendee.user_id">
                                                    <td>
                                                        <img 
                                                            :src="getProfileImageUrl(attendee)" 
                                                            :alt="attendee.name"
                                                            class="profile-img rounded-circle"
                                                            @error="handleImageError"
                                                        />
                                                    </td>
                                                    <td>{{ attendee.name || 'N/A' }}</td>
                                                    <td>{{ attendee.email || 'N/A' }}</td>
                                                    <td>
                                                        <span class="badge bg-info">{{ attendee.schedules_count || 0 }}</span>
                                                        <button class="btn btn-sm btn-link" @click="viewAttendeeSchedules(attendee)" title="View schedules">
                                                            <i class="fas fa-calendar-alt"></i>
                                                        </button>
                                                    </td>
                                                    <td>{{ formatDateTime(attendee.registered_at, eventTimezone) }}</td>
                                                    <td>
                                                        <button class="btn btn-sm btn-danger" @click="removeAudienceMember(attendee.user_id)" :disabled="loading">
                                                            <i class="fas fa-trash"></i> Remove
                                                        </button>
                                                    </td>
                                                </tr>
                                                <tr v-if="audience.length === 0">
                                                    <td colspan="6" class="text-center">No audience yet</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Schedule Modal (Updated with Organizer/Guide selection) -->
                <div class="modal fade" id="scheduleModal" tabindex="-1" data-bs-backdrop="static">
                    <div class="modal-dialog modal-lg">
                        <div class="modal-content">
                            <div class="modal-header">
                                <h5 class="modal-title">{{ isEditingSchedule ? 'Edit' : 'Add' }} Schedule</h5>
                                <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                            </div>
                            <div class="modal-body">
                                <form @submit.prevent="saveSchedule">
                                    <div class="mb-3">
                                        <label class="form-label">Tour Name</label>
                                        <input type="text" class="form-control" v-model="scheduleForm.tour_name" required>
                                    </div>
                                    
                                    <!-- Organizer/Guide Selection -->
                                    <div class="mb-3">
                                        <label class="form-label">Select Guide/Organizer</label>
                                        <select class="form-control" v-model="scheduleForm.organizer_user_id" required>
                                            <option value="">Select a Guide</option>
                                            <option v-for="user in availableUsers" :key="user._id" :value="user._id">
                                                {{ user.name || user.first_name + ' ' + user.last_name }} ({{ user.email }})
                                            </option>
                                        </select>
                                        <small class="text-muted">Select the guide who will lead this virtual tour session</small>
                                    </div>
                                    
                                    <div class="row">
                                        <div class="col-md-6 mb-3">
                                            <label class="form-label">Start Date</label>
                                            <input type="date" class="form-control" v-model="scheduleForm.start_date" required>
                                        </div>
                                        <div class="col-md-6 mb-3">
                                            <label class="form-label">Start Time</label>
                                            <input type="time" class="form-control" v-model="scheduleForm.start_time" required>
                                        </div>
                                    </div>
                                    <div class="row">
                                        <div class="col-md-6 mb-3">
                                            <label class="form-label">End Date</label>
                                            <input type="date" class="form-control" v-model="scheduleForm.end_date" required>
                                        </div>
                                        <div class="col-md-6 mb-3">
                                            <label class="form-label">End Time</label>
                                            <input type="time" class="form-control" v-model="scheduleForm.end_time" required>
                                        </div>
                                    </div>
                                    <div class="mb-3">
                                        <label class="form-label">Description</label>
                                        <textarea class="form-control" v-model="scheduleForm.description" rows="3"></textarea>
                                    </div>
                                    <div class="mb-3">
                                        <label class="form-label">Status</label>
                                        <select class="form-control" v-model="scheduleForm.status">
                                            <option value="scheduled">Scheduled</option>
                                            <option value="ongoing">Ongoing</option>
                                            <option value="completed">Completed</option>
                                            <option value="cancelled">Cancelled</option>
                                        </select>
                                    </div>
                                    <div class="mb-3">
                                        <label class="form-label">Max Attendees (0 for unlimited)</label>
                                        <input type="number" class="form-control" v-model="scheduleForm.max_attendees" min="0">
                                    </div>
                                    <button type="submit" class="btn btn-primary" :disabled="loading">Save Schedule</button>
                                    <button type="button" class="btn btn-secondary ms-2" data-bs-dismiss="modal">Cancel</button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Other modals remain the same -->
                <!-- Add Attendee to Schedule Modal -->
                <div class="modal fade" id="addAttendeeModal" tabindex="-1" data-bs-backdrop="static">
                    <div class="modal-dialog">
                        <div class="modal-content">
                            <div class="modal-header">
                                <h5 class="modal-title">Add Attendee to Schedule: {{ selectedSchedule?.tour_name }}</h5>
                                <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                            </div>
                            <div class="modal-body">
                                <form @submit.prevent="addAttendeeToSchedule">
                                    <div class="mb-3">
                                        <label class="form-label">Select User</label>
                                        <select class="form-control" v-model="selectedAttendeeUserId" required>
                                            <option value="">Select User</option>
                                            <option v-for="user in availableUsers" :key="user._id" :value="user._id">
                                                {{ user.name || user.first_name + ' ' + user.last_name }} ({{ user.email }})
                                            </option>
                                        </select>
                                    </div>
                                    
                                    <div class="alert alert-info" v-if="selectedSchedule">
                                        <strong>Schedule Info:</strong><br>
                                        Guide: {{ selectedSchedule.organizer?.name }}<br>
                                        Current Attendees: {{ selectedSchedule.attendees?.length || 0 }} / 
                                        {{ selectedSchedule.max_attendees || 'Unlimited' }}
                                    </div>
                                    
                                    <button type="submit" class="btn btn-primary" :disabled="loading">Add Attendee</button>
                                    <button type="button" class="btn btn-secondary ms-2" data-bs-dismiss="modal">Cancel</button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Add Audience Member Modal -->
                <div class="modal fade" id="addAudienceModal" tabindex="-1" data-bs-backdrop="static">
                    <div class="modal-dialog">
                        <div class="modal-content">
                            <div class="modal-header">
                                <h5 class="modal-title">Add Audience Member</h5>
                                <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
                            </div>
                            <div class="modal-body">
                                <form @submit.prevent="addAudienceMember">
                                    <div class="mb-3">
                                        <label class="form-label">Select User</label>
                                        <select class="form-control" v-model="selectedAudienceUserId" required>
                                            <option value="">Select User</option>
                                            <option v-for="user in availableUsers" :key="user._id" :value="user._id">
                                                {{ user.name || user.first_name + ' ' + user.last_name }} ({{ user.email }})
                                            </option>
                                        </select>
                                    </div>
                                    
                                    <div class="mb-3">
                                        <label class="form-label">Select Schedule (Optional)</label>
                                        <select class="form-control" v-model="selectedAudienceScheduleId">
                                            <option value="">Add to audience only (no schedule)</option>
                                            <option v-for="schedule in schedules" :key="schedule._id" :value="schedule._id">
                                                {{ schedule.tour_name }} - {{ formatDateTime(schedule.start_date, eventTimezone) }} (Guide: {{ schedule.organizer?.name }})
                                            </option>
                                        </select>
                                        <small class="text-muted">Optionally add the user to a specific schedule</small>
                                    </div>
                                    
                                    <button type="submit" class="btn btn-primary" :disabled="loading">Add Audience Member</button>
                                    <button type="button" class="btn btn-secondary ms-2" data-bs-dismiss="modal">Cancel</button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- View Attendee Schedules Modal -->
                <div class="modal fade" id="attendeeSchedulesModal" tabindex="-1" data-bs-backdrop="static">
                    <div class="modal-dialog modal-lg">
                        <div class="modal-content">
                            <div class="modal-header">
                                <h5 class="modal-title">Schedules for {{ selectedAttendee?.name }}</h5>
                                <div>
                                    <button class="btn btn-sm btn-primary me-2" @click="refreshAttendeeSchedules" :disabled="loading">
                                        <i class="fas fa-sync-alt"></i> Refresh
                                    </button>
                                    <button type="button" class="btn-small" data-bs-dismiss="modal">
                                        <i data-v-459872e3="" class="fas fa-times"></i>
                                    </button>
                                </div>
                            </div>
                            <div class="modal-body">
                                <div class="table-responsive">
                                    <table class="table table-bordered">
                                        <thead>
                                            <tr>
                                                <th>Tour Name</th>
                                                <th>Guide</th>
                                                <th>Start Date & Time</th>
                                                <th>End Date & Time</th>
                                                <th>Status</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr v-for="schedule in attendeeSchedules" :key="schedule._id">
                                                <td>{{ schedule.tour_name }}</td>
                                                <td>{{ schedule.organizer?.name || 'Not Assigned' }}</td>
                                                <td>{{ formatDateTime(schedule.start_date, eventTimezone) }}</td>
                                                <td>{{ formatDateTime(schedule.end_date, eventTimezone) }}</td>
                                                <td>
                                                    <span :class="'badge bg-' + getStatusColor(schedule.status)">
                                                        {{ schedule.status }}
                                                    </span>
                                                </td>
                                                <td>
                                                    <button class="btn btn-sm btn-danger" @click="removeAttendeeFromSchedule(schedule._id)" :disabled="loading">
                                                        <i class="fas fa-user-minus"></i> Remove
                                                    </button>
                                                </td>
                                            </tr>
                                            <tr v-if="attendeeSchedules.length === 0 && !loading">
                                                <td colspan="6" class="text-center">No schedules found for this attendee</td>
                                            </tr>
                                            <tr v-if="loading">
                                                <td colspan="6" class="text-center">
                                                    <i class="fas fa-spinner fa-spin"></i> Loading...
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <FlashMessage group="virtual_tour" />
        </template>
    </GeneralDashboardLayout>
</template>

<script>
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';
import constants from '@/config/constants';
import moment from 'moment-timezone';
import { Modal } from 'bootstrap';
import BackButton from "@/components/Commons/BackButton";
import GeneralDashboardLayout from "@/components/layouts/GeneralDashboardLayout";
import { gettingErrorMessage } from "@/services/GetValidationMessage";

export default {
    name: 'VirtualTourManagement',
    components: {
        BackButton,
        GeneralDashboardLayout,
    },
    data() {
        return {
            eventId: null,
            virtualTour: null,
            activeTab: 'schedules', // Changed default to schedules
            schedules: [],
            audience: [],
            availableUsers: [],
            eventTimezone: 'UTC',
            showScheduleModal: false,
            isEditingSchedule: false,
            scheduleForm: {
                tour_name: '',
                organizer_user_id: '', // Store selected organizer ID
                start_date: '',
                start_time: '',
                end_date: '',
                end_time: '',
                description: '',
                status: 'scheduled',
                max_attendees: 0
            },
            currentScheduleId: null,
            loading: false,
            scheduleModal: null,
            addAttendeeModal: null,
            addAudienceModal: null,
            attendeeSchedulesModal: null,
            selectedSchedule: null,
            selectedAttendeeUserId: '',
            selectedAudienceUserId: '',
            selectedAudienceScheduleId: '',
            selectedAttendee: null,
            attendeeSchedules: [],
            currentAttendeeId: null,
            defaultProfileImage: '/assets/img/male.png' 
        }
    },
    async created() {
        this.eventId = this.$route.params.event_id;
        await this.fetchEventDetails();
        await this.fetchVirtualTour();
        await this.fetchAvailableUsers();
    },
    mounted() {
        // Initialize modals
        this.scheduleModal = new Modal(document.getElementById('scheduleModal'));
        this.addAttendeeModal = new Modal(document.getElementById('addAttendeeModal'));
        this.addAudienceModal = new Modal(document.getElementById('addAudienceModal'));
        this.attendeeSchedulesModal = new Modal(document.getElementById('attendeeSchedulesModal'));
    },
    beforeUnmount() {
        // Clean up modals
        if (this.scheduleModal) {
            this.scheduleModal.dispose();
        }
        if (this.addAttendeeModal) {
            this.addAttendeeModal.dispose();
        }
        if (this.addAudienceModal) {
            this.addAudienceModal.dispose();
        }
        if (this.attendeeSchedulesModal) {
            this.attendeeSchedulesModal.dispose();
        }
        // Clean up flash message group
        delete this.$flashMessage.groups.virtual_tour;
    },
    methods: {
        getAuthHeaders() {
            return {
                'Authorization': `Bearer ${localStorage.getItem('token')}`,
                'Content-Type': 'application/json'
            };
        },

        showFlashMessage(type, message, title = null) {
            const flashConfig = {
                id: uuidv4(),
                group: 'virtual_tour',
                type: type,
                time: 3000,
                html: `<ul><li>${message}</li></ul>`
            };
            
            if (title) {
                flashConfig.title = title;
            }
            
            this.$flashMessage.show(flashConfig);
        },

        getProfileImageUrl(user) {
            let profilePic = null;
            
            if (user.profile_picture && user.profile_picture.key) {
                profilePic = user.profile_picture;
            } else if (user.user_details && user.user_details.profile_picture && user.user_details.profile_picture.key) {
                profilePic = user.user_details.profile_picture;
            } else if (user.profilePicture && user.profilePicture.key) {
                profilePic = user.profilePicture;
            }
            
            if (profilePic && profilePic.key) {
                return `${constants.IMAGE_URL}${profilePic.key}`;
            }
            
            return this.defaultProfileImage;
        },

        getOrganizerImageUrl(organizer) {
            if (!organizer) return this.defaultProfileImage;
            if (organizer.profile_picture && organizer.profile_picture.key) {
                return `${constants.IMAGE_URL}${organizer.profile_picture.key}`;
            }
            return this.defaultProfileImage;
        },

        handleImageError(event) {
            event.target.src = this.defaultProfileImage;
        },

        async fetchEventDetails() {
            try {
                const response = await axios.get(`${constants.API_URL}/api/events/event-id/${this.eventId}`, {
                    headers: this.getAuthHeaders()
                });
                this.eventTimezone = response.data.data.time_zone || 'UTC';
                moment.tz.setDefault(this.eventTimezone);
            } catch (error) {
                console.error('Error fetching event details:', error);
                this.showFlashMessage('error', 'Failed to fetch event details');
            }
        },

        async fetchVirtualTour() {
            try {
                const response = await axios.get(`${constants.API_URL}/api/virtual-tour/event/${this.eventId}`, {
                    headers: this.getAuthHeaders()
                });
                
                if (response.data.status === 'success' && response.data.data) {
                    this.virtualTour = response.data.data;
                    
                    // Process schedules
                    const rawSchedules = this.virtualTour.schedules || [];
                    this.schedules = rawSchedules.map(schedule => {
                        const attendees = (schedule.attendees || []).map(attendee => {
                            let normalizedUserId = null;
                            if (attendee.user_id) {
                                if (typeof attendee.user_id === 'object' && attendee.user_id._id) {
                                    normalizedUserId = attendee.user_id._id.toString();
                                } else if (typeof attendee.user_id === 'string') {
                                    normalizedUserId = attendee.user_id;
                                } else if (attendee.user_id.toString) {
                                    normalizedUserId = attendee.user_id.toString();
                                }
                            }
                            
                            return {
                                ...attendee,
                                user_id: normalizedUserId,
                                name: attendee.name || attendee.user_details?.name,
                                email: attendee.email || attendee.user_details?.email,
                                profile_picture: attendee.profile_picture || attendee.user_details?.profile_picture
                            };
                        });
                        
                        return {
                            ...schedule,
                            attendees: attendees
                        };
                    });
                    
                    console.log("Schedules loaded:", this.schedules);
                    await this.fetchAudience();
                }
            } catch (error) {
                console.error('Error fetching virtual tour:', error);
                if (error.response?.status !== 404) {
                    this.showFlashMessage('error', 'Failed to fetch virtual tour');
                }
            }
        },

        async fetchAvailableUsers() {
            try {
                const response = await axios.get(`${constants.API_URL}/api/event-user/active/${this.eventId}`, {
                    headers: this.getAuthHeaders()
                });
                this.availableUsers = response.data.data || [];
            } catch (error) {
                console.error('Error fetching users:', error);
                this.showFlashMessage('error', 'Failed to fetch available users');
            }
        },

        async fetchAudience() {
            if (!this.virtualTour) return;
            try {
                const response = await axios.get(`${constants.API_URL}/api/virtual-tour/${this.virtualTour._id}/audience`, {
                    headers: this.getAuthHeaders()
                });
                this.audience = response.data.data || [];
            } catch (error) {
                console.error('Error fetching audience:', error);
            }
        },

        async createNewTour() {
            this.loading = true;
            try {
                const response = await axios.post(`${constants.API_URL}/api/virtual-tour`, {
                    event_id: this.eventId,
                    tour_name: 'Virtual Tour',
                    status: 'draft'
                }, { headers: this.getAuthHeaders() });
                
                if (response.data.status === 'success') {
                    this.virtualTour = response.data.data;
                    this.showFlashMessage('success', 'Virtual tour created successfully');
                    await this.fetchVirtualTour();
                }
            } catch (error) {
                console.error('Error creating tour:', error);
                const errorMessage = gettingErrorMessage(error);
                this.showFlashMessage('error', errorMessage || 'Failed to create virtual tour');
            } finally {
                this.loading = false;
            }
        },

        openScheduleModal(schedule = null) {
            if (schedule) {
                this.isEditingSchedule = true;
                this.currentScheduleId = schedule._id;
                const startDate = moment.utc(schedule.start_date).tz(this.eventTimezone);
                const endDate = moment.utc(schedule.end_date).tz(this.eventTimezone);
                this.scheduleForm = {
                    tour_name: schedule.tour_name,
                    organizer_user_id: schedule.organizer?.user_id || '',
                    start_date: startDate.format('YYYY-MM-DD'),
                    start_time: startDate.format('HH:mm'),
                    end_date: endDate.format('YYYY-MM-DD'),
                    end_time: endDate.format('HH:mm'),
                    description: schedule.description || '',
                    status: schedule.status,
                    max_attendees: schedule.max_attendees || 0
                };
            } else {
                this.isEditingSchedule = false;
                this.currentScheduleId = null;
                const now = moment.tz(this.eventTimezone);
                const tomorrow = now.clone().add(1, 'day');
                this.scheduleForm = {
                    tour_name: '',
                    organizer_user_id: '',
                    start_date: now.format('YYYY-MM-DD'),
                    start_time: now.format('HH:00'),
                    end_date: tomorrow.format('YYYY-MM-DD'),
                    end_time: tomorrow.clone().add(1, 'hour').format('HH:00'),
                    description: '',
                    status: 'scheduled',
                    max_attendees: 0
                };
            }
            this.scheduleModal.show();
        },

        async saveSchedule() {
            if (!this.scheduleForm.tour_name) {
                this.showFlashMessage('error', 'Please enter tour name');
                return;
            }
            if (!this.scheduleForm.organizer_user_id) {
                this.showFlashMessage('error', 'Please select a guide/organizer');
                return;
            }
            if (!this.scheduleForm.start_date || !this.scheduleForm.start_time) {
                this.showFlashMessage('error', 'Please select start date and time');
                return;
            }
            if (!this.scheduleForm.end_date || !this.scheduleForm.end_time) {
                this.showFlashMessage('error', 'Please select end date and time');
                return;
            }

            this.loading = true;
            try {
                const startMoment = moment.tz(
                    `${this.scheduleForm.start_date} ${this.scheduleForm.start_time}`,
                    this.eventTimezone
                );
                const endMoment = moment.tz(
                    `${this.scheduleForm.end_date} ${this.scheduleForm.end_time}`,
                    this.eventTimezone
                );
                
                if (endMoment.isSameOrBefore(startMoment)) {
                    this.showFlashMessage('error', 'End date/time must be after start date/time');
                    return;
                }

                // Get organizer details
                const selectedOrganizer = this.availableUsers.find(
                    user => user._id === this.scheduleForm.organizer_user_id
                );

                const scheduleData = {
                    tour_name: this.scheduleForm.tour_name,
                    organizer: {
                        user_id: this.scheduleForm.organizer_user_id,
                        name: selectedOrganizer?.name || selectedOrganizer?.first_name + ' ' + selectedOrganizer?.last_name,
                        email: selectedOrganizer?.email,
                        profile_picture: selectedOrganizer?.profile_picture || null,
                        role: 'Guide'
                    },
                    start_date: startMoment.utc().toISOString(),
                    end_date: endMoment.utc().toISOString(),
                    duration: endMoment.diff(startMoment, 'minutes'),
                    description: this.scheduleForm.description,
                    status: this.scheduleForm.status,
                    max_attendees: parseInt(this.scheduleForm.max_attendees) || 0
                };

                let response;
                if (this.isEditingSchedule) {
                    response = await axios.put(
                        `${constants.API_URL}/api/virtual-tour/${this.virtualTour._id}/schedules/${this.currentScheduleId}`,
                        scheduleData,
                        { headers: this.getAuthHeaders() }
                    );
                } else {
                    response = await axios.post(
                        `${constants.API_URL}/api/virtual-tour/${this.virtualTour._id}/schedules`,
                        scheduleData,
                        { headers: this.getAuthHeaders() }
                    );
                }

                if (response.data.status === 'success') {
                    await this.fetchVirtualTour();
                    this.scheduleModal.hide();
                    this.showFlashMessage('success', `Schedule ${this.isEditingSchedule ? 'updated' : 'added'} successfully`);
                }
            } catch (error) {
                console.error('Error saving schedule:', error);
                const errorMessage = gettingErrorMessage(error);
                this.showFlashMessage('error', errorMessage || 'Failed to save schedule');
            } finally {
                this.loading = false;
            }
        },

        async deleteSchedule(scheduleId) {
            if (!confirm('Are you sure you want to delete this schedule?')) return;
            
            this.loading = true;
            try {
                await axios.delete(`${constants.API_URL}/api/virtual-tour/${this.virtualTour._id}/schedules/${scheduleId}`, {
                    headers: this.getAuthHeaders()
                });
                await this.fetchVirtualTour();
                this.showFlashMessage('success', 'Schedule deleted successfully');
            } catch (error) {
                console.error('Error deleting schedule:', error);
                const errorMessage = gettingErrorMessage(error);
                this.showFlashMessage('error', errorMessage || 'Failed to delete schedule');
            } finally {
                this.loading = false;
            }
        },

        openAddAttendeeModal(schedule) {
            this.selectedSchedule = schedule;
            this.selectedAttendeeUserId = '';
            this.addAttendeeModal.show();
        },

        async addAttendeeToSchedule() {
            if (!this.selectedAttendeeUserId) {
                this.showFlashMessage('error', 'Please select a user');
                return;
            }

            if (this.selectedSchedule.max_attendees > 0 && 
                this.selectedSchedule.attendees?.length >= this.selectedSchedule.max_attendees) {
                this.showFlashMessage('error', 'Schedule has reached maximum attendees limit');
                return;
            }

            this.loading = true;
            try {
                const response = await axios.post(
                    `${constants.API_URL}/api/virtual-tour/${this.virtualTour._id}/schedules/${this.selectedSchedule._id}/attendees/${this.selectedAttendeeUserId}`,
                    {},
                    { headers: this.getAuthHeaders() }
                );
                
                if (response.data.status === 'success') {
                    await this.fetchVirtualTour();
                    await this.fetchAudience();
                    this.addAttendeeModal.hide();
                    this.showFlashMessage('success', 'Attendee added to schedule successfully');
                    this.$forceUpdate();
                }
            } catch (error) {
                console.error('Error adding attendee:', error);
                const errorMessage = error.response?.data?.message || error.message || 'Failed to add attendee to schedule';
                this.showFlashMessage('error', errorMessage);
            } finally {
                this.loading = false;
            }
        },

        openAddAudienceModal() {
            this.selectedAudienceUserId = '';
            this.selectedAudienceScheduleId = '';
            this.addAudienceModal.show();
        },

        async addAudienceMember() {
            if (!this.selectedAudienceUserId) {
                this.showFlashMessage('error', 'Please select a user');
                return;
            }

            this.loading = true;
            try {
                if (this.selectedAudienceScheduleId) {
                    await axios.post(
                        `${constants.API_URL}/api/virtual-tour/${this.virtualTour._id}/schedules/${this.selectedAudienceScheduleId}/attendees/${this.selectedAudienceUserId}`,
                        {},
                        { headers: this.getAuthHeaders() }
                    );
                    this.showFlashMessage('success', 'Audience member added to schedule successfully');
                } else {
                    this.showFlashMessage('info', 'User added to audience. To add to a schedule, use the schedule attendees button.');
                }
                
                await this.fetchAudience();
                this.addAudienceModal.hide();
            } catch (error) {
                console.error('Error adding audience member:', error);
                this.showFlashMessage('error', 'Failed to add audience member, Schedule is full or User already registered for this schedule');
            } finally {
                this.loading = false;
            }
        },

        async removeAudienceMember(userId) {
            if (!confirm('Are you sure you want to remove this audience member from all schedules?')) return;
            
            this.loading = true;
            try {
                const userSchedules = this.schedules.filter(schedule => 
                    schedule.attendees?.some(attendee => attendee.user_id === userId)
                );
                
                for (const schedule of userSchedules) {
                    await axios.delete(
                        `${constants.API_URL}/api/virtual-tour/${this.virtualTour._id}/schedules/${schedule._id}/attendees/${userId}`,
                        { headers: this.getAuthHeaders() }
                    );
                }
                
                await this.fetchVirtualTour();
                this.showFlashMessage('success', 'Audience member removed successfully');
            } catch (error) {
                console.error('Error removing audience member:', error);
                const errorMessage = gettingErrorMessage(error);
                this.showFlashMessage('error', errorMessage || 'Failed to remove audience member');
            } finally {
                this.loading = false;
            }
        },

        async viewAttendeeSchedules(attendee) {
            this.selectedAttendee = attendee;
            this.loading = true;
        
            try {
                await this.fetchVirtualTour();
                
                let attendeeId = null;
                if (typeof attendee.user_id === 'object' && attendee.user_id._id) {
                    attendeeId = attendee.user_id._id.toString();
                } else if (typeof attendee.user_id === 'string') {
                    attendeeId = attendee.user_id;
                } else if (attendee._id) {
                    attendeeId = attendee._id;
                }
                
                this.attendeeSchedules = this.schedules.filter(schedule => {
                    if (!schedule.attendees || schedule.attendees.length === 0) {
                        return false;
                    }
                    
                    return schedule.attendees.some(attendeeItem => {
                        let scheduleAttendeeId = null;
                        
                        if (typeof attendeeItem.user_id === 'object' && attendeeItem.user_id) {
                            scheduleAttendeeId = attendeeItem.user_id._id?.toString() || attendeeItem.user_id.toString();
                        } else if (typeof attendeeItem.user_id === 'string') {
                            scheduleAttendeeId = attendeeItem.user_id;
                        } else if (attendeeItem._id) {
                            scheduleAttendeeId = attendeeItem._id;
                        }
                        
                        if (attendeeItem.user_details && attendeeItem.user_details._id) {
                            scheduleAttendeeId = attendeeItem.user_details._id.toString();
                        }
                        
                        return scheduleAttendeeId === attendeeId;
                    });
                });
                
                if (this.attendeeSchedules.length === 0) {
                    this.showFlashMessage('info', 'This attendee is not enrolled in any schedules yet');
                }
                
                this.currentAttendeeId = attendeeId;
                this.attendeeSchedulesModal.show();
            } catch (error) {
                console.error('Error viewing attendee schedules:', error);
                this.showFlashMessage('error', 'Failed to load attendee schedules');
            } finally {
                this.loading = false;
            }
        },

        async removeAttendeeFromSchedule(scheduleId) {
            if (!confirm(`Remove ${this.selectedAttendee?.name} from this schedule?`)) return;
            
            this.loading = true;
            try {
                await axios.delete(
                    `${constants.API_URL}/api/virtual-tour/${this.virtualTour._id}/schedules/${scheduleId}/attendees/${this.currentAttendeeId}`,
                    { headers: this.getAuthHeaders() }
                );
                
                this.attendeeSchedules = this.attendeeSchedules.filter(s => s._id !== scheduleId);
                await this.fetchVirtualTour();
                this.showFlashMessage('success', 'Attendee removed from schedule successfully');
            } catch (error) {
                console.error('Error removing attendee from schedule:', error);
                const errorMessage = gettingErrorMessage(error);
                this.showFlashMessage('error', errorMessage || 'Failed to remove attendee from schedule');
            } finally {
                this.loading = false;
            }
        },

        formatDateTime(date, timezone) {
            if (!date) return '-';
            return moment.utc(date).tz(timezone).format('YYYY-MM-DD hh:mm A');
        },

        getStatusColor(status) {
            const colors = {
                scheduled: 'primary',
                ongoing: 'warning',
                completed: 'success',
                cancelled: 'danger'
            };
            return colors[status] || 'secondary';
        },

        async refreshAttendeeSchedules() {
            if (!this.selectedAttendee) return;
            
            this.loading = true;
            try {
                await this.fetchVirtualTour();
                
                let attendeeId = null;
                if (typeof this.selectedAttendee.user_id === 'object' && this.selectedAttendee.user_id._id) {
                    attendeeId = this.selectedAttendee.user_id._id.toString();
                } else if (typeof this.selectedAttendee.user_id === 'string') {
                    attendeeId = this.selectedAttendee.user_id;
                } else if (this.selectedAttendee._id) {
                    attendeeId = this.selectedAttendee._id;
                }
                
                this.attendeeSchedules = this.schedules.filter(schedule => {
                    if (!schedule.attendees || schedule.attendees.length === 0) return false;
                    return schedule.attendees.some(attendeeItem => {
                        let scheduleAttendeeId = null;
                        if (typeof attendeeItem.user_id === 'string') {
                            scheduleAttendeeId = attendeeItem.user_id;
                        } else if (attendeeItem.user_id && attendeeItem.user_id.toString) {
                            scheduleAttendeeId = attendeeItem.user_id.toString();
                        }
                        return scheduleAttendeeId === attendeeId;
                    });
                });
                
                if (this.attendeeSchedules.length === 0) {
                    this.showFlashMessage('info', 'This attendee is not enrolled in any schedules yet');
                } else {
                    this.showFlashMessage('success', `Found ${this.attendeeSchedules.length} schedule(s)`);
                }
            } catch (error) {
                console.error('Error refreshing schedules:', error);
                this.showFlashMessage('error', 'Failed to refresh schedules');
            } finally {
                this.loading = false;
            }
        },
    }
}
</script>

<style scoped>
.virtual-tour-management {
    padding: 20px;
}

.card {
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
    margin-bottom: 20px;
}

.card-header {
    background-color: #f8f9fa;
    border-bottom: 1px solid #dee2e6;
}

.card-header h5 {
    margin: 0;
    font-weight: 600;
}

.nav-tabs {
    border-bottom: 2px solid #dee2e6;
}

.nav-tabs .nav-link {
    border: none;
    color: #6c757d;
    font-weight: 500;
    padding: 12px 20px;
    transition: all 0.3s;
    cursor: pointer;
}

.nav-tabs .nav-link i {
    margin-right: 8px;
}

.nav-tabs .nav-link:hover {
    color: #007bff;
    background: transparent;
}

.nav-tabs .nav-link.active {
    color: #007bff;
    border-bottom: 2px solid #007bff;
    background: transparent;
}

.table-responsive {
    overflow-x: auto;
}

.table th {
    font-weight: 600;
    background-color: #f8f9fa;
    border-bottom: 2px solid #dee2e6;
}

.table td {
    vertical-align: middle;
}

.badge {
    padding: 5px 10px;
    font-size: 12px;
}

.btn-sm {
    padding: 4px 8px;
}

.modal-lg {
    max-width: 800px;
}

.modal-body {
    max-height: 70vh;
    overflow-y: auto;
}

.profile-img {
    width: 40px;
    height: 40px;
    object-fit: cover;
    border: 2px solid #dee2e6;
}

.profile-img-sm {
    width: 32px;
    height: 32px;
    object-fit: cover;
    border: 1px solid #dee2e6;
    border-radius: 50%;
}

.me-2 {
    margin-right: 8px;
}

@media (max-width: 768px) {
    .virtual-tour-management {
        padding: 10px;
    }
    
    .table-responsive {
        font-size: 14px;
    }
    
    .nav-tabs .nav-link {
        padding: 8px 12px;
        font-size: 12px;
    }
    
    .nav-tabs .nav-link i {
        margin-right: 4px;
    }
    
    .profile-img, .profile-img-sm {
        width: 30px;
        height: 30px;
    }
}
</style>